import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import {
  ArrowLeft,
  Plus,
  Trash2,
  Pencil,
  Star,
  X as XIcon,
  Save,
  UploadCloud,
  Image as ImageIcon,
} from "lucide-react";
import Nav from "@/components/Nav";
import Footer from "@/components/Footer";
import { useAuth } from "@/context/AuthContext";
import { JOURNAL_ADMIN } from "@/constants/testIds";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORIES = [
  "Campaign of the Day",
  "Brand Stories",
  "AI in Creativity",
  "Creative Trends",
  "Advertising",
  "Influencer Marketing",
  "Offline Campaigns",
  "Film Production",
  "Events & Experiences",
  "Technology",
  "Design",
  "Culture",
  "Producer Notes",
  "Case Studies",
];

const BACKEND = process.env.REACT_APP_BACKEND_URL || "";
const ALLOWED_TYPES = ["image/jpeg", "image/jpg", "image/png", "image/webp", "image/heic", "image/heif", "image/gif"];
const MAX_MB = 10;

function saveButtonLabel(saving, isEditing) {
  if (saving) return "Saving…";
  if (isEditing) return "Save changes";
  return "Publish story";
}

function DropzoneContent({ uploading, progress }) {
  if (uploading) {
    return (
      <>
        <UploadCloud className="w-6 h-6 text-denim-bnm animate-pulse" />
        <div className="w-full max-w-[280px]">
          <div className="h-1 bg-black/10 rounded-full overflow-hidden">
            <div
              className="h-full bg-denim-bnm transition-all"
              style={{ width: `${progress}%` }}
            />
          </div>
          <div className="text-[12px] text-muted-ink mt-2 text-center">
            Uploading… {progress}%
          </div>
        </div>
      </>
    );
  }
  return (
    <>
      <div className="w-11 h-11 rounded-full border border-bnm bg-white flex items-center justify-center">
        <ImageIcon className="w-5 h-5 text-ink" />
      </div>
      <div className="text-center">
        <div className="font-display font-semibold text-[16px] text-ink">
          Drop a cover image here
        </div>
        <div className="text-[13px] text-muted-ink mt-1">
          Or <span className="text-ink underline underline-offset-2">click to browse</span>
        </div>
      </div>
    </>
  );
}

function CoverUploader({ value, onChange }) {
  const inputRef = useRef(null);
  const [dragOver, setDragOver] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState("");

  const upload = useCallback(async (file) => {
    setError("");
    if (!file) return;
    if (!ALLOWED_TYPES.includes(file.type)) {
      setError("Only JPEG, PNG, WEBP, HEIC or GIF images are supported.");
      return;
    }
    if (file.size > MAX_MB * 1024 * 1024) {
      setError(`Image is too large. Max ${MAX_MB} MB.`);
      return;
    }
    const fd = new FormData();
    fd.append("file", file);
    setUploading(true);
    setProgress(0);
    try {
      const { data } = await axios.post(
        `${BACKEND}/api/journal/admin/upload-cover`,
        fd,
        {
          headers: { "Content-Type": "multipart/form-data" },
          onUploadProgress: (evt) => {
            if (evt.total) setProgress(Math.round((evt.loaded / evt.total) * 100));
          },
        }
      );
      const url = data?.url ? `${BACKEND}${data.url}` : "";
      if (url) onChange(url);
    } catch (e) {
      setError(e?.response?.data?.detail || "Upload failed. Try a different image.");
    } finally {
      setUploading(false);
      setProgress(0);
    }
  }, [onChange]);

  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer?.files?.[0];
    if (file) upload(file);
  };

  const clear = () => {
    onChange("");
    setError("");
    if (inputRef.current) inputRef.current.value = "";
  };

  return (
    <div>
      <div className="flex items-baseline justify-between mb-2">
        <span className="eyebrow eyebrow-muted">Cover image</span>
        <span className="text-[11.5px] text-muted-ink">JPEG · PNG · WEBP · HEIC · up to {MAX_MB} MB</span>
      </div>

      {value ? (
        <div className="bnm-card p-3 flex flex-col sm:flex-row gap-4 items-start">
          <div className="w-full sm:w-[220px] aspect-[16/10] bg-[#f4f4f4] rounded-lg overflow-hidden shrink-0">
            <img
              src={value}
              alt="cover preview"
              className="w-full h-full object-cover"
              referrerPolicy="no-referrer"
              onError={(e) => { e.currentTarget.style.display = "none"; }}
            />
          </div>
          <div className="flex-1 flex flex-col gap-3 min-w-0">
            <div className="text-[13px] text-muted-ink break-all" data-testid="admin-cover-current-url">
              {value}
            </div>
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                onClick={() => inputRef.current?.click()}
                className="pill text-[12.5px] py-1.5 px-3 inline-flex items-center gap-1.5"
                data-testid="admin-cover-replace"
              >
                <UploadCloud className="w-3.5 h-3.5" />
                <span>Replace</span>
              </button>
              <button
                type="button"
                onClick={clear}
                className="pill text-[12.5px] py-1.5 px-3 inline-flex items-center gap-1.5 hover:border-denim-bnm hover:text-denim-bnm"
                data-testid="admin-cover-remove"
              >
                <XIcon className="w-3.5 h-3.5" />
                <span>Remove</span>
              </button>
            </div>
          </div>
        </div>
      ) : (
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          onDragEnter={(e) => { e.preventDefault(); setDragOver(true); }}
          onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={onDrop}
          disabled={uploading}
          data-testid="admin-cover-dropzone"
          className={`w-full rounded-xl border-2 border-dashed transition-all duration-200 text-left px-6 py-10 flex flex-col items-center justify-center gap-3 ${
            dragOver
              ? "border-denim-bnm bg-[#f4f6fa]"
              : "border-bnm bg-[#fafafa] hover:border-black hover:bg-white"
          } ${uploading ? "opacity-80 cursor-wait" : "cursor-pointer"}`}
        >
          <DropzoneContent uploading={uploading} progress={progress} />
        </button>
      )}

      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        className="hidden"
        data-testid="admin-cover-file-input"
        onChange={(e) => {
          const f = e.target.files?.[0];
          if (f) upload(f);
        }}
      />

      <div className="mt-4">
        <label className="block">
          <span className="text-[11.5px] eyebrow eyebrow-muted">Or paste an image URL</span>
          <input
            type="url"
            value={value}
            onChange={(e) => onChange(e.target.value)}
            data-testid={JOURNAL_ADMIN.coverInput}
            placeholder="https://…"
            className="mt-2 w-full h-11 px-4 rounded-xl border border-bnm text-[14px] focus:outline-none focus:border-black"
          />
        </label>
      </div>

      {error && (
        <p className="text-[13px] text-denim-bnm mt-3" data-testid="admin-cover-error">
          {error}
        </p>
      )}
    </div>
  );
}

const EMPTY_ARTICLE = {
  id: null,
  title: "",
  dek: "",
  body: "",
  producer_take: "",
  category: "Campaign of the Day",
  tags: [],
  cover_image: "",
  author: "Brand New Money Editorial",
  is_featured: false,
  status: "published",
  brand: "",
  industry: "",
  country: "",
  year: new Date().getFullYear(),
};

function Field({ label, hint, children }) {
  return (
    <label className="block">
      <div className="flex items-baseline justify-between mb-2">
        <span className="eyebrow eyebrow-muted">{label}</span>
        {hint && <span className="text-[11.5px] text-muted-ink">{hint}</span>}
      </div>
      {children}
    </label>
  );
}

function ArticleForm({ article, onCancel, onSave, saving }) {
  const [form, setForm] = useState({
    ...EMPTY_ARTICLE,
    ...article,
    tags: (article?.tags || []).join(", "),
  });

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = (e) => {
    e.preventDefault();
    const payload = {
      ...form,
      tags: form.tags
        .split(",")
        .map((t) => t.trim())
        .filter(Boolean),
      year: form.year ? Number(form.year) : null,
    };
    onSave(payload);
  };

  return (
    <form
      onSubmit={submit}
      className="bnm-card p-6 md:p-8 space-y-6"
      data-testid={JOURNAL_ADMIN.form}
    >
      <div className="flex items-start justify-between gap-4">
        <h2 className="font-display font-extrabold text-[24px] md:text-[32px] tracking-[-0.03em] leading-[1.1] text-ink">
          {article?.id ? "Edit story" : "New story"}
        </h2>
        <button
          type="button"
          onClick={onCancel}
          data-testid={JOURNAL_ADMIN.cancelButton}
          className="w-9 h-9 rounded-full border border-bnm flex items-center justify-center hover:border-black transition-colors"
          aria-label="Cancel"
        >
          <XIcon className="w-4 h-4" />
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <div className="md:col-span-2">
          <Field label="Headline">
            <input
              required
              value={form.title}
              onChange={(e) => set("title", e.target.value)}
              data-testid={JOURNAL_ADMIN.titleInput}
              placeholder="How Apple's ‘Shot on iPhone' turned every user into a cinematographer"
              className="w-full h-12 px-4 rounded-xl border border-bnm text-[15px] focus:outline-none focus:border-black transition-colors"
            />
          </Field>
        </div>

        <div className="md:col-span-2">
          <Field label="Standfirst (short summary)">
            <textarea
              rows={2}
              value={form.dek}
              onChange={(e) => set("dek", e.target.value)}
              data-testid={JOURNAL_ADMIN.dekInput}
              placeholder="One evocative sentence that makes someone click."
              className="w-full px-4 py-3 rounded-xl border border-bnm text-[14.5px] focus:outline-none focus:border-black transition-colors resize-y"
            />
          </Field>
        </div>

        <div className="md:col-span-2">
          <Field label="Body" hint="Separate paragraphs with a blank line">
            <textarea
              required
              rows={12}
              value={form.body}
              onChange={(e) => set("body", e.target.value)}
              data-testid={JOURNAL_ADMIN.bodyInput}
              className="w-full px-4 py-3 rounded-xl border border-bnm text-[14.5px] leading-[1.7] focus:outline-none focus:border-black transition-colors resize-y"
            />
          </Field>
        </div>

        <div className="md:col-span-2">
          <Field label="Producer's Take" hint="Editorial voice, what we'd tell a founder">
            <textarea
              rows={6}
              value={form.producer_take}
              onChange={(e) => set("producer_take", e.target.value)}
              data-testid={JOURNAL_ADMIN.producerTakeInput}
              className="w-full px-4 py-3 rounded-xl border border-bnm text-[14.5px] leading-[1.7] focus:outline-none focus:border-black transition-colors resize-y"
            />
          </Field>
        </div>

        <Field label="Category">
          <select
            required
            value={form.category}
            onChange={(e) => set("category", e.target.value)}
            data-testid={JOURNAL_ADMIN.categorySelect}
            className="w-full h-12 px-4 rounded-xl border border-bnm text-[14.5px] focus:outline-none focus:border-black bg-white"
          >
            {CATEGORIES.map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </select>
        </Field>

        <div className="md:col-span-2">
          <CoverUploader
            value={form.cover_image}
            onChange={(v) => set("cover_image", v)}
          />
        </div>

        <div className="md:col-span-2">
          <Field label="Tags (comma separated)">
            <input
              value={form.tags}
              onChange={(e) => set("tags", e.target.value)}
              data-testid={JOURNAL_ADMIN.tagsInput}
              placeholder="Apple, iPhone, Cinematography"
              className="w-full h-12 px-4 rounded-xl border border-bnm text-[14.5px] focus:outline-none focus:border-black"
            />
          </Field>
        </div>

        <Field label="Brand">
          <input
            value={form.brand || ""}
            onChange={(e) => set("brand", e.target.value)}
            data-testid={JOURNAL_ADMIN.brandInput}
            className="w-full h-12 px-4 rounded-xl border border-bnm text-[14.5px] focus:outline-none focus:border-black"
          />
        </Field>

        <Field label="Industry">
          <input
            value={form.industry || ""}
            onChange={(e) => set("industry", e.target.value)}
            data-testid={JOURNAL_ADMIN.industryInput}
            className="w-full h-12 px-4 rounded-xl border border-bnm text-[14.5px] focus:outline-none focus:border-black"
          />
        </Field>

        <Field label="Country">
          <input
            value={form.country || ""}
            onChange={(e) => set("country", e.target.value)}
            data-testid={JOURNAL_ADMIN.countryInput}
            className="w-full h-12 px-4 rounded-xl border border-bnm text-[14.5px] focus:outline-none focus:border-black"
          />
        </Field>

        <Field label="Year">
          <input
            type="number"
            value={form.year || ""}
            onChange={(e) => set("year", e.target.value)}
            data-testid={JOURNAL_ADMIN.yearInput}
            className="w-full h-12 px-4 rounded-xl border border-bnm text-[14.5px] focus:outline-none focus:border-black"
          />
        </Field>

        <Field label="Status">
          <select
            value={form.status}
            onChange={(e) => set("status", e.target.value)}
            data-testid={JOURNAL_ADMIN.statusSelect}
            className="w-full h-12 px-4 rounded-xl border border-bnm text-[14.5px] focus:outline-none focus:border-black bg-white"
          >
            <option value="draft">Draft</option>
            <option value="published">Published</option>
          </select>
        </Field>

        <label className="flex items-center gap-3 self-end pb-2">
          <input
            type="checkbox"
            checked={!!form.is_featured}
            onChange={(e) => set("is_featured", e.target.checked)}
            data-testid={JOURNAL_ADMIN.featuredToggle}
            className="w-4 h-4 accent-black"
          />
          <span className="text-[14px] text-ink inline-flex items-center gap-1.5">
            <Star className="w-3.5 h-3.5 text-denim-bnm" />
            Featured on Journal home
          </span>
        </label>
      </div>

      <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 pt-2">
        <button
          type="submit"
          disabled={saving}
          data-testid={JOURNAL_ADMIN.saveButton}
          className="btn-denim disabled:opacity-70"
        >
          <Save className="w-4 h-4" />
          <span>{saveButtonLabel(saving, !!article?.id)}</span>
        </button>
        <button
          type="button"
          onClick={onCancel}
          className="btn-ghost"
          data-testid={`${JOURNAL_ADMIN.cancelButton}-secondary`}
        >
          <span>Cancel</span>
        </button>
      </div>
    </form>
  );
}

export default function JournalAdmin() {
  const { user, loading: authLoading, signIn } = useAuth();
  const navigate = useNavigate();
  const [isAdmin, setIsAdmin] = useState(null); // null=checking, true/false
  const [articles, setArticles] = useState([]);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  const checkAdmin = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/journal/admin/whoami`);
      setIsAdmin(!!data?.is_admin);
    } catch {
      setIsAdmin(false);
    }
  }, []);

  const loadArticles = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/journal/admin/articles`);
      setArticles(data?.items || []);
    } catch {
      setArticles([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (authLoading) return;
    checkAdmin();
  }, [authLoading, checkAdmin, user]);

  useEffect(() => {
    if (isAdmin) loadArticles();
  }, [isAdmin, loadArticles]);

  const handleSave = async (payload) => {
    setSaving(true);
    try {
      if (editing?.id) {
        await axios.put(`${API}/journal/admin/articles/${editing.id}`, payload);
      } else {
        await axios.post(`${API}/journal/admin/articles`, payload);
      }
      setEditing(null);
      await loadArticles();
    } catch (e) {
      // eslint-disable-next-line no-alert
      alert(e?.response?.data?.detail || "Could not save the story.");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (a) => {
    // eslint-disable-next-line no-alert
    if (!window.confirm(`Delete "${a.title}"? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/journal/admin/articles/${a.id}`);
      await loadArticles();
    } catch (err) {
      // eslint-disable-next-line no-alert
      alert(err?.response?.data?.detail || "Could not delete the story.");
    }
  };

  const grouped = useMemo(() => {
    const drafts = articles.filter((a) => a.status === "draft");
    const published = articles.filter((a) => a.status === "published");
    return { drafts, published };
  }, [articles]);

  if (authLoading || isAdmin === null) {
    return (
      <div className="min-h-screen bg-white text-ink">
        <Nav />
        <div className="max-w-[720px] mx-auto px-6 py-32 text-muted-ink text-[14px] text-center">
          Checking access…
        </div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="min-h-screen bg-white text-ink" data-testid={JOURNAL_ADMIN.page}>
        <Nav />
        <div className="max-w-[640px] mx-auto px-6 md:px-10 py-32 text-center">
          <div className="eyebrow eyebrow-denim mb-4">Admin</div>
          <h1 className="font-display font-extrabold text-[36px] md:text-[52px] tracking-[-0.04em] leading-[1.05] text-ink">
            Sign in to edit the <span className="font-serif-italic font-light">Journal</span>.
          </h1>
          <button
            type="button"
            onClick={signIn}
            className="btn-denim mt-10"
          >
            <span>Sign in</span>
          </button>
        </div>
        <Footer />
      </div>
    );
  }

  if (!isAdmin) {
    return (
      <div className="min-h-screen bg-white text-ink" data-testid={JOURNAL_ADMIN.page}>
        <Nav />
        <div className="max-w-[640px] mx-auto px-6 md:px-10 py-32 text-center">
          <div className="eyebrow eyebrow-denim mb-4">Restricted</div>
          <h1 className="font-display font-extrabold text-[32px] md:text-[44px] tracking-[-0.04em] leading-[1.05] text-ink">
            The Journal editor is invitation-only.
          </h1>
          <p className="text-muted-ink text-[15px] mt-6 max-w-[420px] mx-auto">
            You&rsquo;re signed in as <span className="italic">{user.email}</span>.
            This inbox isn&rsquo;t on the editor list.
          </p>
          <Link to="/journal" className="btn-ghost mt-10 inline-flex">
            <span>Read the Journal instead</span>
          </Link>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white text-ink" data-testid={JOURNAL_ADMIN.page}>
      <Nav />
      <div className="max-w-[1180px] mx-auto px-6 md:px-10 pt-12 md:pt-20 pb-24">
        <button
          type="button"
          onClick={() => navigate("/journal")}
          className="inline-flex items-center gap-2 text-muted-ink text-[13px] hover:text-ink transition-colors mb-8"
        >
          <ArrowLeft className="w-4 h-4" /> Back to Journal
        </button>

        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12">
          <div>
            <div className="eyebrow eyebrow-denim mb-4">Editor</div>
            <h1 className="font-display font-extrabold text-[36px] md:text-[56px] tracking-[-0.04em] leading-[1.02] text-ink">
              The <span className="font-serif-italic font-light">Journal</span> desk.
            </h1>
            <p className="text-muted-ink text-[15px] mt-4 max-w-[520px]">
              Publish. Revise. Save drafts. Every story here is written and
              reviewed by you, never by machines.
            </p>
          </div>
          {!editing && (
            <button
              type="button"
              onClick={() => setEditing({ ...EMPTY_ARTICLE })}
              className="btn-denim"
              data-testid={JOURNAL_ADMIN.newButton}
            >
              <Plus className="w-4 h-4" />
              <span>New story</span>
            </button>
          )}
        </div>

        <AdminBody
          editing={editing}
          loading={loading}
          saving={saving}
          grouped={grouped}
          onSave={handleSave}
          onCancel={() => setEditing(null)}
          onEdit={(a) => setEditing(a)}
          onDelete={handleDelete}
        />
      </div>
      <Footer />
    </div>
  );
}

function AdminBody({ editing, loading, saving, grouped, onSave, onCancel, onEdit, onDelete }) {
  if (editing) {
    return (
      <ArticleForm
        article={editing}
        saving={saving}
        onCancel={onCancel}
        onSave={onSave}
      />
    );
  }
  if (loading) {
    return <div className="text-muted-ink text-[14px]">Loading…</div>;
  }
  return (
    <div className="space-y-14">
      {grouped.drafts.length > 0 && (
        <section>
          <h2 className="eyebrow eyebrow-denim mb-4">Drafts</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {grouped.drafts.map((a) => (
              <ArticleRow
                key={a.id}
                a={a}
                onEdit={() => onEdit(a)}
                onDelete={() => onDelete(a)}
              />
            ))}
          </div>
        </section>
      )}

      <section>
        <h2 className="eyebrow eyebrow-denim mb-4">
          Published <span className="text-muted-ink font-normal">, {grouped.published.length}</span>
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {grouped.published.map((a) => (
            <ArticleRow
              key={a.id}
              a={a}
              onEdit={() => onEdit(a)}
              onDelete={() => onDelete(a)}
            />
          ))}
        </div>
        {grouped.published.length === 0 && grouped.drafts.length === 0 && (
          <div className="text-muted-ink text-[14px] mt-4">
            No stories yet. Hit &ldquo;New story&rdquo; to publish the first one.
          </div>
        )}
      </section>
    </div>
  );
}

function ArticleRow({ a, onEdit, onDelete }) {
  return (
    <div
      className="bnm-card p-5 flex gap-4"
      data-testid={JOURNAL_ADMIN.articleRow}
    >
      <div className="w-24 h-24 shrink-0 bg-[#f4f4f4] overflow-hidden rounded-lg">
        {a.cover_image && (
          <img
            src={a.cover_image}
            alt=""
            onError={(e) => {
              e.currentTarget.style.display = "none";
            }}
            className="w-full h-full object-cover"
            referrerPolicy="no-referrer"
          />
        )}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-1">
          <span className="eyebrow eyebrow-denim">{a.category}</span>
          {a.is_featured && (
            <span className="inline-flex items-center gap-1 text-[11px] text-denim-bnm">
              <Star className="w-3 h-3" /> Featured
            </span>
          )}
          {a.status === "draft" && (
            <span className="text-[11px] text-muted-ink italic">Draft</span>
          )}
        </div>
        <h3 className="font-display font-extrabold text-[16px] md:text-[18px] tracking-[-0.02em] leading-[1.2] text-ink truncate">
          {a.title}
        </h3>
        {a.dek && (
          <p className="text-[13px] text-muted-ink line-clamp-2 mt-1.5">
            {a.dek}
          </p>
        )}
        <div className="mt-3 flex items-center gap-2">
          <button
            type="button"
            onClick={onEdit}
            className="pill text-[12.5px] py-1.5 px-3 inline-flex items-center gap-1.5"
            data-testid={JOURNAL_ADMIN.editButton}
          >
            <Pencil className="w-3 h-3" />
            <span>Edit</span>
          </button>
          <button
            type="button"
            onClick={onDelete}
            className="pill text-[12.5px] py-1.5 px-3 inline-flex items-center gap-1.5 hover:border-denim-bnm hover:text-denim-bnm"
            data-testid={JOURNAL_ADMIN.deleteButton}
          >
            <Trash2 className="w-3 h-3" />
            <span>Delete</span>
          </button>
        </div>
      </div>
    </div>
  );
}
